import React, { useState, useEffect } from 'react'
import { Modal, message, Tooltip } from 'antd'
import ReactPlayer from 'react-player'
import {
  DownloadOutlined,
  LinkOutlined,
  FullscreenOutlined,
  CloseOutlined,
  LoadingOutlined
} from '@ant-design/icons'

import './AiVideoModal.scss'

const AIVideoModal = ({
  visible,
  onClose,
  videoUrl,
  title = 'AI Generated Video'
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)

  const handleDownload = async () => {
    setDownloadLoading(true)

    try {
      const response = await fetch(videoUrl, {
        method: 'GET',
        headers: {
          Accept: 'video/mp4,video/webm,video/*'
        }
      })

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`)

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.mp4`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      message.success('Video downloaded successfully!')
    } catch (error) {
      console.error('Download failed:', error)
      try {
        const link = document.createElement('a')
        link.href = videoUrl
        link.target = '_blank'
        link.download = `${title.replace(
          /[^a-zA-Z0-9]/g,
          '_'
        )}_${Date.now()}.mp4`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        message.info(
          'Download started - if it fails, the URL may not support it'
        )
      } catch {
        window.open(videoUrl, '_blank', 'noopener,noreferrer')
        message.warning('Opened video in new tab. Right-click to save.')
      }
    } finally {
      setDownloadLoading(false)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(videoUrl)
      message.success('Video link copied to clipboard!')
    } catch (err) {
      console.error('Copy failed:', err)
      message.error('Failed to copy link')
    }
  }

  const toggleFullscreen = () => {
    const element = document.querySelector('.ai-video-modal .ant-modal-content')
    if (!document.fullscreenElement) {
      element && element.requestFullscreen && element.requestFullscreen()
    } else {
      document.exitFullscreen && document.exitFullscreen()
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width="90vw"
      centered
      className="ai-video-modal"
      closable={false}
      bodyStyle={{ padding: 0 }}
      destroyOnClose
    >
      <div className="ai-viewer-layout">
        {/* Top Section: Dedicated Header Bar */}
        <div className="viewer-header">
          <div className="video-meta">
            <h2 className="video-title">{title}</h2>
            <span className="video-badge">AI GENERATED</span>
          </div>

          <div className="viewer-actions">
            <Tooltip title="Copy Link" placement="bottom">
              <button className="action-button" onClick={handleCopyLink}>
                <LinkOutlined />
              </button>
            </Tooltip>

            <Tooltip title="Download" placement="bottom">
              <button
                className="action-button"
                onClick={handleDownload}
                disabled={downloadLoading}
              >
                {downloadLoading ? <LoadingOutlined /> : <DownloadOutlined />}
              </button>
            </Tooltip>

            <Tooltip
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              placement="bottom"
            >
              <button className="action-button" onClick={toggleFullscreen}>
                <FullscreenOutlined />
              </button>
            </Tooltip>

            <div className="action-divider"></div>

            <Tooltip title="Close Player" placement="bottom">
              <button className="close-button" onClick={onClose}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Bottom Section: Video Stage (No overlays) */}
        <div className={`viewer-stage ${isFullscreen ? 'fullscreen' : ''}`}>
          {/* Loading Indicator */}
          {isLoading && (
            <div className="loading-stage">
              <div className="custom-loader">
                <div className="loader-spinner">
                  <LoadingOutlined />
                </div>
                <div className="loader-content">
                  <h3 className="loader-title">Preparing Video</h3>
                  <p className="loader-subtitle">
                    Crafting your AI masterpiece...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Video Player */}
          <ReactPlayer
            key={videoUrl}
            url={videoUrl ? videoUrl.split('#')[0] : ''}
            width="100%"
            height="100%"
            playing
            controls
            onReady={() => setIsLoading(false)}
            onError={e => {
              console.error('Video player error details:', e)
              const videoElement = document.querySelector(
                '.ai-viewer-layout video'
              )
              if (videoElement) {
                videoElement.removeAttribute('crossorigin')
              }
              setIsLoading(false)
            }}
            config={{
              file: {
                forceVideo: true,
                attributes: {
                  controlsList: 'nodownload',
                  disablePictureInPicture: true
                }
              }
            }}
          />
        </div>
      </div>
    </Modal>
  )
}

export default AIVideoModal
